import React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Chip, Stack } from '@mui/material';
import DiffContext from '../DiffViewer/DiffContext';

export default function FixDialog({ open, loading, fixedFile, fixToken, isPRActive, onDiscard, onApply, onClose }) {
  const [showLongWait, setShowLongWait] = React.useState(false);
  const [view, setView] = React.useState('diff'); // 'diff' | 'summary' | 'fixes'
  React.useEffect(() => {
    let timer;
    if (loading) {
      setShowLongWait(false);
      timer = setTimeout(() => { setShowLongWait(true); }, 45000);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [loading]);
  const noChange = fixedFile && fixedFile.original && typeof fixedFile.content === 'string' && fixedFile.content.trim() === fixedFile.original.trim();
  const fixes = Array.isArray(fixedFile?.fixes) ? fixedFile.fixes : [];
  const summary = typeof fixedFile?.summary === 'string' ? fixedFile.summary : '';
  const canApply = !!fixToken && !!isPRActive && !noChange;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { backgroundColor: '#161b22', border: '1px solid #30363d', borderRadius: '6px' } }}>
      <DialogTitle sx={{ color: '#f0f6fc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {loading ? 'Generating Fix...' : 'Generated Fix'}
        {!loading && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label="Code" variant={view==='diff'?'filled':'outlined'} onClick={() => setView('diff')} sx={{ color: '#f0f6fc', borderColor: '#30363d' }} />
            <Chip size="small" label="Summary" variant={view==='summary'?'filled':'outlined'} onClick={() => setView('summary')} sx={{ color: '#f0f6fc', borderColor: '#30363d' }} />
            <Chip size="small" label={`Fixes${fixes.length?` (${fixes.length})`:''}`} variant={view==='fixes'?'filled':'outlined'} onClick={() => setView('fixes')} sx={{ color: '#f0f6fc', borderColor: '#30363d' }} />
          </Stack>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, gap: 1.5 }}>
            <CircularProgress sx={{ color: '#58a6ff' }} />
            <Box>
              <Typography sx={{ color: '#c9d1d9' }}>Generating fixes...</Typography>
              {showLongWait && (
                <Typography variant="body2" sx={{ color: '#7d8590', mt: 1 }}>
                  Fix is being generated, please wait up to 3 minutes…
                </Typography>
              )}
            </Box>
          </Box>
        ) : fixedFile ? (
          view === 'summary' ? (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#f0f6fc' }}>Summary</Typography>
              <Typography sx={{ color: '#c9d1d9', whiteSpace: 'pre-wrap' }}>{summary || 'No summary available.'}</Typography>
            </Box>
          ) : view === 'fixes' ? (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#f0f6fc' }}>Fixes</Typography>
              {fixes.length ? fixes.map((f, i) => (
                <Typography key={i} sx={{ color: '#c9d1d9', mb: 0.5 }}>- {f}</Typography>
              )) : (
                <Typography sx={{ color: '#c9d1d9' }}>No fixes listed.</Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#f0f6fc' }}>Fixed File: {fixedFile.file_path}</Typography>
              {noChange && (
                <Typography variant="body2" sx={{ color: '#7d8590', mb: 1 }}>No changes detected in this file.</Typography>
              )}
              <DiffContext beforeContent={fixedFile.original} afterContent={fixedFile.content} />
            </Box>
          )
        ) : (
          <Typography sx={{ color: '#f0f6fc' }}>
            Generating fix failed due to large file size. Please fix it manually.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {fixToken ? (
          <>
            <Button onClick={onDiscard} sx={{ color: '#f85149', '&:hover': { backgroundColor: '#f8514920' } }}>Discard</Button>
            <Button onClick={onApply} variant="contained" sx={{ backgroundColor: '#3fb950', '&:hover': { backgroundColor: '#2ea043' } }} disabled={!canApply}>Apply</Button>
          </>
        ) : (
          <Button onClick={onClose} variant="outlined" sx={{ color: '#58a6ff', borderColor: '#58a6ff' }}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}




